import React from 'react';
import { motion } from 'framer-motion';
import { XIcon, DatabaseIcon, BotIcon, UsersIcon, TrendingUpIcon, DollarSignIcon, PercentIcon, PackageIcon, BriefcaseIcon, ActivityIcon, AlertCircleIcon, CheckCircle2Icon, BuildingIcon, SparklesIcon } from 'lucide-react';
import { Account } from '../types';
interface AnatomyPanelProps {
  account: Account;
  onClose: () => void;
}
export function AnatomyPanel({
  account,
  onClose
}: AnatomyPanelProps) {
  // Calculate team composition
  const quadTeamCount = account.quad_team?.length || 0;
  const clientContactsCount = account.client_contacts?.length || 0;
  const totalTeam = quadTeamCount + clientContactsCount;
  // Mock financial data (in a real app, this would come from the account data)
  const quarterlyRevenue = 1250000 + Math.floor(Math.random() * 500000);
  const averageMargin = 18 + Math.floor(Math.random() * 12);
  // Calculate product footprint percentages - normalize to 100%
  const rawFootprint = account.footprint || account.products_services.map(product => ({
    product,
    utilization: 50 + Math.floor(Math.random() * 50)
  }));
  // Normalize percentages to sum to 100%
  const totalUtilization = rawFootprint.reduce((sum, item) => sum + item.utilization, 0);
  const productFootprint = rawFootprint.map(item => ({
    product: item.product,
    utilization: Math.round(item.utilization / totalUtilization * 100)
  }));
  // Get workstreams
  const workstreams = account.workstreams || [];
  // Get recent orders
  const recentOrders = account.orders?.slice(0, 3) || [];
  // Get current time dynamically
  const currentTime = new Date().toLocaleString();
  return <>
      {/* Backdrop */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" data-id="element-152" />
      {/* Panel */}
      <motion.div initial={{
      x: '100%'
    }} animate={{
      x: 0
    }} exit={{
      x: '100%'
    }} transition={{
      type: 'spring',
      damping: 30,
      stiffness: 300
    }} className="fixed right-0 top-0 bottom-0 w-full md:w-[480px] bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 border-l border-slate-800/80 z-50 flex flex-col shadow-2xl overflow-hidden" data-id="element-153">
        {/* Decorative gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 pointer-events-none" data-id="element-154" />
        {/* Header */}
        <div className="relative flex-shrink-0 px-6 py-5 border-b border-slate-800/80 bg-slate-950/80 backdrop-blur-xl" data-id="element-155">
          <div className="flex items-center justify-between" data-id="element-156">
            <div className="flex items-center gap-3" data-id="element-157">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center shadow-lg shadow-purple-600/30" data-id="element-158">
                <SparklesIcon className="w-5 h-5 text-white" data-id="element-159" />
              </div>
              <div data-id="element-160">
                <h3 className="text-lg font-bold text-white" data-id="element-161">
                  Account Anatomy
                </h3>
                <p className="text-xs text-slate-400 font-medium" data-id="element-162">
                  Structural Overview
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-white transition-all rounded-lg hover:bg-slate-800/60" data-id="element-163">
              <XIcon className="w-5 h-5" data-id="element-164" />
            </button>
          </div>
        </div>
        {/* Content - Scrollable */}
        <div className="relative flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent" data-id="element-165">
          <div className="p-6 space-y-5" data-id="element-166">
            {/* Financial Overview */}
            <div className="bg-slate-950/50 border border-slate-800/60 rounded-xl p-5 space-y-4" data-id="element-167">
              <div className="flex items-center gap-2 mb-3" data-id="element-168">
                <DollarSignIcon className="w-4 h-4 text-green-400" data-id="element-169" />
                <h4 className="text-sm font-bold text-white uppercase tracking-wider" data-id="element-170">
                  Financial Overview
                </h4>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-171">
                <div className="bg-slate-900/60 rounded-lg p-4" data-id="element-172">
                  <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider font-bold" data-id="element-173">
                    Q4 Revenue
                  </p>
                  <p className="text-2xl font-bold text-white" data-id="element-174">
                    ${(quarterlyRevenue / 1000000).toFixed(2)}M
                  </p>
                  <div className="flex items-center gap-1 mt-2" data-id="element-175">
                    <TrendingUpIcon className="w-3 h-3 text-green-400" data-id="element-176" />
                    <span className="text-xs text-green-400 font-semibold" data-id="element-177">
                      +12% YoY
                    </span>
                  </div>
                </div>
                <div className="bg-slate-900/60 rounded-lg p-4" data-id="element-178">
                  <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider font-bold" data-id="element-179">
                    Avg Margin
                  </p>
                  <p className="text-2xl font-bold text-white" data-id="element-180">
                    {averageMargin}%
                  </p>
                  <div className="flex items-center gap-1 mt-2" data-id="element-181">
                    <PercentIcon className="w-3 h-3 text-blue-400" data-id="element-182" />
                    <span className="text-xs text-slate-400 font-semibold" data-id="element-183">
                      Target: 20%
                    </span>
                  </div>
                </div>
              </div>
            </div>
            {/* Team Composition */}
            <div className="bg-slate-950/50 border border-slate-800/60 rounded-xl p-5 space-y-4" data-id="element-184">
              <div className="flex items-center gap-2 mb-3" data-id="element-185">
                <UsersIcon className="w-4 h-4 text-purple-400" data-id="element-186" />
                <h4 className="text-sm font-bold text-white uppercase tracking-wider" data-id="element-187">
                  Team Composition
                </h4>
              </div>
              <div className="space-y-3" data-id="element-188">
                <div className="flex items-center justify-between" data-id="element-189">
                  <div className="flex items-center gap-2" data-id="element-190">
                    <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center" data-id="element-191">
                      <BriefcaseIcon className="w-4 h-4 text-purple-400" data-id="element-192" />
                    </div>
                    <span className="text-sm text-slate-300 font-medium" data-id="element-193">
                      Quad Team
                    </span>
                  </div>
                  <span className="text-lg font-bold text-white" data-id="element-194">
                    {quadTeamCount}
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-195">
                  <div className="flex items-center gap-2" data-id="element-196">
                    <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center" data-id="element-197">
                      <BuildingIcon className="w-4 h-4 text-blue-400" data-id="element-198" />
                    </div>
                    <span className="text-sm text-slate-300 font-medium" data-id="element-199">
                      Client Contacts
                    </span>
                  </div>
                  <span className="text-lg font-bold text-white" data-id="element-200">
                    {clientContactsCount}
                  </span>
                </div>
                <div className="pt-3 border-t border-slate-800/60" data-id="element-201">
                  <div className="flex items-center justify-between" data-id="element-202">
                    <span className="text-xs text-slate-500 font-bold uppercase tracking-wider" data-id="element-203">
                      Total Team
                    </span>
                    <span className="text-xl font-bold text-white" data-id="element-204">
                      {totalTeam}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            {/* Products & Services Footprint */}
            <div className="bg-slate-950/50 border border-slate-800/60 rounded-xl p-5 space-y-4" data-id="element-205">
              <div className="flex items-center gap-2 mb-3" data-id="element-206">
                <PackageIcon className="w-4 h-4 text-amber-400" data-id="element-207" />
                <h4 className="text-sm font-bold text-white uppercase tracking-wider" data-id="element-208">
                  Product Footprint
                </h4>
              </div>
              <div className="space-y-3" data-id="element-209">
                {productFootprint.map(item => <div key={item.product} data-id="element-210">
                    <div className="flex items-center justify-between mb-1.5" data-id="element-211">
                      <span className="text-xs text-slate-400 font-medium" data-id="element-212">
                        {item.product}
                      </span>
                      <span className="text-xs text-white font-bold" data-id="element-213">
                        {item.utilization}%
                      </span>
                    </div>
                    <div className="h-2 bg-slate-900/60 rounded-full overflow-hidden" data-id="element-214">
                      <motion.div initial={{
                    width: 0
                  }} animate={{
                    width: `${item.utilization}%`
                  }} transition={{
                    duration: 1,
                    delay: 0.2
                  }} className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full" data-id="element-215" />
                    </div>
                  </div>)}
              </div>
            </div>
            {/* Chatbots & Knowledge Spaces */}
            <div className="bg-slate-950/50 border border-slate-800/60 rounded-xl p-5 space-y-4" data-id="element-216">
              <div className="flex items-center gap-2 mb-3" data-id="element-217">
                <BotIcon className="w-4 h-4 text-blue-400" data-id="element-218" />
                <h4 className="text-sm font-bold text-white uppercase tracking-wider" data-id="element-219">
                  Chatbots
                </h4>
              </div>
              <div className="space-y-3" data-id="element-220">
                {account.bots.map(bot => <div key={bot.name} className="bg-slate-900/60 rounded-lg p-3" data-id="element-221">
                    <div className="flex items-center justify-between mb-2" data-id="element-222">
                      <span className="text-sm text-white font-semibold" data-id="element-223">
                        {bot.name}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded-md font-bold ${bot.type === 'external' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'}`} data-id="element-224">
                        {bot.type}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-slate-500" data-id="element-225">
                      <DatabaseIcon className="w-3 h-3" data-id="element-226" />
                      <span data-id="element-227">
                        {bot.bound_spaces.length} Knowledge Space
                        {bot.bound_spaces.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>)}
              </div>
            </div>
            {/* Active Workstreams */}
            {workstreams.length > 0 && <div className="bg-slate-950/50 border border-slate-800/60 rounded-xl p-5 space-y-4" data-id="element-228">
                <div className="flex items-center gap-2 mb-3" data-id="element-229">
                  <ActivityIcon className="w-4 h-4 text-green-400" data-id="element-230" />
                  <h4 className="text-sm font-bold text-white uppercase tracking-wider" data-id="element-231">
                    Active Workstreams
                  </h4>
                </div>
                <div className="space-y-2" data-id="element-232">
                  {workstreams.map((workstream, index) => <div key={index} className="bg-slate-900/60 rounded-lg p-3" data-id="element-233">
                      <div className="flex items-center justify-between mb-1" data-id="element-234">
                        <span className="text-sm text-white font-medium" data-id="element-235">
                          {workstream.name}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-md font-bold ${workstream.status === 'Active' ? 'bg-green-500/20 text-green-400' : workstream.status === 'At Risk' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`} data-id="element-236">
                          {workstream.status}
                        </span>
                      </div>
                      {workstream.kpi && <p className="text-xs text-slate-500" data-id="element-237">
                          {workstream.kpi}
                        </p>}
                    </div>)}
                </div>
              </div>}
            {/* Key Performance Indicators */}
            <div className="bg-slate-950/50 border border-slate-800/60 rounded-xl p-5 space-y-4" data-id="element-238">
              <div className="flex items-center gap-2 mb-3" data-id="element-239">
                <TrendingUpIcon className="w-4 h-4 text-blue-400" data-id="element-240" />
                <h4 className="text-sm font-bold text-white uppercase tracking-wider" data-id="element-241">
                  Key Metrics
                </h4>
              </div>
              <div className="grid grid-cols-3 gap-3" data-id="element-242">
                <div className="bg-slate-900/60 rounded-lg p-3 text-center" data-id="element-243">
                  <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider font-bold" data-id="element-244">
                    Active Jobs
                  </p>
                  <p className="text-2xl font-bold text-white" data-id="element-245">
                    {account.kpis.active_jobs}
                  </p>
                </div>
                <div className="bg-slate-900/60 rounded-lg p-3 text-center" data-id="element-246">
                  <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider font-bold" data-id="element-247">
                    Open Quotes
                  </p>
                  <p className="text-2xl font-bold text-white" data-id="element-248">
                    {account.kpis.open_quotes}
                  </p>
                </div>
                <div className="bg-slate-900/60 rounded-lg p-3 text-center" data-id="element-249">
                  <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider font-bold" data-id="element-250">
                    SLA Alerts
                  </p>
                  <p className={`text-2xl font-bold ${account.kpis.sla_alerts > 0 ? 'text-red-400' : 'text-green-400'}`} data-id="element-251">
                    {account.kpis.sla_alerts}
                  </p>
                </div>
              </div>
            </div>
            {/* Recent Orders */}
            {recentOrders.length > 0 && <div className="bg-slate-950/50 border border-slate-800/60 rounded-xl p-5 space-y-4" data-id="element-252">
                <div className="flex items-center gap-2 mb-3" data-id="element-253">
                  <PackageIcon className="w-4 h-4 text-purple-400" data-id="element-254" />
                  <h4 className="text-sm font-bold text-white uppercase tracking-wider" data-id="element-255">
                    Recent Orders
                  </h4>
                </div>
                <div className="space-y-2" data-id="element-256">
                  {recentOrders.map(order => <div key={order.id} className="bg-slate-900/60 rounded-lg p-3" data-id="element-257">
                      <div className="flex items-center justify-between mb-1" data-id="element-258">
                        <span className="text-sm text-white font-medium" data-id="element-259">
                          {order.title}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-md font-bold ${order.status === 'Delivered' ? 'bg-green-500/20 text-green-400' : order.status === 'In Progress' ? 'bg-blue-500/20 text-blue-400' : order.status === 'On Hold' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-slate-500/20 text-slate-400'}`} data-id="element-260">
                          {order.status}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-slate-500" data-id="element-261">
                        <span data-id="element-262">${(order.value / 1000).toFixed(0)}K</span>
                        <span data-id="element-263">{new Date(order.date).toLocaleDateString()}</span>
                      </div>
                    </div>)}
                </div>
              </div>}
            {/* Account Health */}
            <div className="bg-slate-950/50 border border-slate-800/60 rounded-xl p-5 space-y-4" data-id="element-264">
              <div className="flex items-center gap-2 mb-3" data-id="element-265">
                <AlertCircleIcon className="w-4 h-4 text-emerald-400" data-id="element-266" />
                <h4 className="text-sm font-bold text-white uppercase tracking-wider" data-id="element-267">
                  Health Status
                </h4>
              </div>
              <div className="space-y-3" data-id="element-268">
                <div className="flex items-center justify-between" data-id="element-269">
                  <span className="text-sm text-slate-400" data-id="element-270">Account Health</span>
                  <span className={`px-3 py-1.5 rounded-lg text-xs font-bold border shadow-lg ${account.health === 'Green' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40' : account.health === 'Yellow' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40' : 'bg-red-500/20 text-red-400 border-red-500/40'}`} data-id="element-271">
                    {account.health}
                  </span>
                </div>
                {account.margin_health && <div className="flex items-center justify-between" data-id="element-272">
                    <span className="text-sm text-slate-400" data-id="element-273">
                      Margin Health
                    </span>
                    <span className={`px-3 py-1.5 rounded-lg text-xs font-bold border shadow-lg ${account.margin_health === 'Healthy' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40' : account.margin_health === 'Moderate' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40' : 'bg-red-500/20 text-red-400 border-red-500/40'}`} data-id="element-274">
                      {account.margin_health}
                    </span>
                  </div>}
              </div>
            </div>
          </div>
        </div>
        {/* Footer */}
        <div className="relative flex-shrink-0 px-6 py-4 border-t border-slate-800/80 bg-slate-950/80 backdrop-blur-xl" data-id="element-275">
          <div className="flex items-center justify-between text-xs text-slate-500" data-id="element-276">
            <span data-id="element-277">Last synced: {currentTime}</span>
            <button onClick={onClose} className="px-4 py-2 bg-slate-800/60 hover:bg-slate-700/60 border border-slate-700/50 hover:border-slate-600 text-white rounded-lg transition-all font-semibold text-sm" data-id="element-278">
              Close
            </button>
          </div>
        </div>
      </motion.div>
    </>;
}